import type { Directive, DirectiveBinding } from "vue";
import { useDocumentStore } from "@/stores/documents";
import { useTilesStore } from "@/stores/tiles";
import { useSectionsStore } from "@/stores/sections";
import { useAssignmentsStore } from "@/stores/assignments";

interface HoverPrefetchUrlValue {
  url: string;
  [key: string]: unknown;
}

interface HoverPrefetchElement extends HTMLElement {
  __prefetchStart?: () => Promise<void>;
  __prefetchStop?: () => Promise<void>;
}

type ActionParameters = Record<string, unknown>;

type StoreAction =
  | { type: "useDocumentStore"; function: string; parameters?: ActionParameters }
  | { type: "useSectionsStore"; function: string; parameters?: ActionParameters }
  | { type: "useTilesStore"; function: string; parameters?: ActionParameters }
  | { type: "useAssignmentsStore"; function: string; parameters?: ActionParameters }
  // Vuex action — kept for backward compat
  | { type: "vuex"; action: string; parameters?: ActionParameters }
  // Legacy/unknown action shape — preserve runtime behaviour without throwing
  | { type: string; action?: string; function?: string; parameters?: ActionParameters };

type DynamicMethodMap = Record<string, (payload?: unknown) => Promise<unknown> | unknown>;

interface InstanceWithStore {
  $store: {
    dispatch: (action: string, payload?: unknown) => Promise<unknown>;
  };
}

const HoverPrefetchUrl: Directive<HTMLElement, HoverPrefetchUrlValue> = {
  beforeMount(el, binding: DirectiveBinding<HoverPrefetchUrlValue>) {
    let timeOutInstance: ReturnType<typeof setTimeout> | null = null;
    let isFetchingDone = false;
    const target = el as HoverPrefetchElement;

    const prefetch = async () => {
      if (!isFetchingDone && binding.value.url) {
        try {
          const actionFlow = urlToActionMap[binding.value.url];
          isFetchingDone = true;
          let payload: ActionParameters | undefined;
          if (actionFlow) {
            for (const act of actionFlow) {
              if (act.parameters) {
                payload = Object.assign(act.parameters);
                const keys = Object.keys(payload!);
                keys.forEach(key => {
                  const bindingValue = binding.value as Record<string, unknown>;
                  if (bindingValue[key]) {
                    payload![key] = bindingValue[key];
                  }
                });
              }

              if (act.type === "vuex" && "action" in act && act.action) {
                const instance = binding.instance as unknown as InstanceWithStore;
                await instance.$store.dispatch(act.action, payload);
              }
              if (act.type === "useDocumentStore" && act.function) {
                const documentStore = useDocumentStore() as unknown as DynamicMethodMap;
                if (act.parameters) {
                  await documentStore[act.function](payload);
                } else {
                  const instance = binding.instance as unknown as InstanceWithStore;
                  await documentStore[act.function](instance.$store);
                }
              }
              if (act.type === "useTilesStore" && act.function) {
                const tilesStore = useTilesStore() as unknown as DynamicMethodMap;
                const instance = binding.instance as unknown as InstanceWithStore;
                await tilesStore[act.function](instance.$store);
              }
              if (act.type === "useSectionsStore" && act.function) {
                const sectionsStore = useSectionsStore() as unknown as DynamicMethodMap;
                await sectionsStore[act.function](payload);
              }
              if (act.type === "useAssignmentsStore" && act.function) {
                const assignmentsStore = useAssignmentsStore() as unknown as DynamicMethodMap;
                await assignmentsStore[act.function](payload);
              }
            }
          }
          // eslint-disable-next-line no-empty
        } catch {} // Its okay to fail here - it's just a prefetch
      }
    };

    target.__prefetchStart = async () => {
      if (!timeOutInstance) {
        timeOutInstance = setTimeout(async () => {
          await prefetch();
          timeOutInstance = null;
        }, 100);
      }
    };

    target.__prefetchStop = async () => {
      if (timeOutInstance) {
        clearTimeout(timeOutInstance);
      }
      timeOutInstance = null;
    };

    if (binding && binding.value.url && urlToActionMap[binding.value.url]) {
      el.addEventListener("mouseenter", target.__prefetchStart, true);
      el.addEventListener("mouseleave", target.__prefetchStop, true);
    }
  },

  unmounted(el) {
    const target = el as HoverPrefetchElement;
    if (target.__prefetchStart) {
      el.removeEventListener("mouseenter", target.__prefetchStart, true);
    }
    if (target.__prefetchStop) {
      el.removeEventListener("mouseleave", target.__prefetchStop, true);
    }
    delete target.__prefetchStart;
    delete target.__prefetchStop;
  }
};

const urlToActionMap: Record<string, StoreAction[]> = {
  sectionDocuments: [
    {
      type: "useSectionsStore",
      function: "loadSystemDrivesForSection",
      parameters: { sectionId: "", forceReload: false }
    },
    { type: "useDocumentStore", function: "loadSectionDrivesRoot", parameters: { sectionId: "" } }
  ],
  "/sections": [
    { type: "useSectionStore", action: "loadMySections" },
    { type: "useTilesStore", action: "loadTilesForMySections", parameters: {} }
  ],
  sectionAssignments: [
    {
      type: "useAssignmentsStore",
      function: "loadForSection",
      parameters: { extSysApiSectionId: "", sectionId: "" }
    }
  ],
  "/mydocuments": [
    { type: "useDocumentStore", function: "loadLegacyDrive" },
    { type: "useDocumentStore", function: "loadMyOneDriveRoot" }
  ]
};

export default HoverPrefetchUrl;
